import { Link } from "react-router-dom";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Clock, CheckCircle2, PlusCircle, TrendingUp, TrendingDown } from "lucide-react";
import { useAppData } from "../data/AppDataContext";
import { naira, formatDate } from "../data/format";

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

export default function Dashboard() {
  const { dashboardStats, loading } = useAppData();
  const stats = dashboardStats;

  if (loading && !stats) return <div className="pl-empty">Loading…</div>;
  if (!stats) return null;

  const series = stats.collections_series || [];
  const todayTotal = series[series.length - 1]?.amount || 0;
  const yesterdayTotal = series[series.length - 2]?.amount || 0;
  const trendUp = todayTotal >= yesterdayTotal;
  const trendPct = yesterdayTotal ? Math.round(((todayTotal - yesterdayTotal) / yesterdayTotal) * 100) : null;

  return (
    <div>
      <div className="pl-page-title">{greeting()}, OTIC Admin </div>
      <div className="pl-page-sub">Here's what's happening with your payments today.</div>

      <div className="pl-stat-card" style={{ maxWidth: 280, marginBottom: 20 }}>
        <div className="pl-stat-value red">{naira(stats.total_outstanding)}</div>
        <div className="pl-stat-label">Outstanding Balance</div>
      </div>

      <div className="pl-stat-grid">
        <div className="pl-stat-card">
          <div className="pl-stat-value blue">{naira(stats.today_collections)}</div>
          <div className="pl-stat-label">Today's Collections</div>
          {trendPct !== null && (
            <div className={`pl-stat-trend ${trendUp ? "up" : "down"}`}>
              {trendUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {Math.abs(trendPct)}% from yesterday
            </div>
          )}
        </div>
        <div className="pl-stat-card">
          <div className="pl-stat-value" style={{ color: "var(--green-700)" }}>{naira(stats.total_collected)}</div>
          <div className="pl-stat-label">Collected This Year</div>
        </div>
        <div className="pl-stat-card">
          <div className="pl-stat-value" style={{ color: "var(--amber-700)" }}>{stats.pending_count}</div>
          <div className="pl-stat-label">Pending Confirmations</div>
        </div>
        <div className="pl-stat-card">
          <div className="pl-stat-value">{stats.total_customers}</div>
          <div className="pl-stat-label">Total Customers</div>
        </div>
      </div>

      <div className="pl-dashboard-row">
        <div className="pl-panel" style={{ padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div className="pl-section-title" style={{ margin: 0 }}>Outstanding Payments</div>
            <Link to="/customers?ordering=balance_desc" className="pl-btn-link">View all →</Link>
          </div>
          {(!stats.top_outstanding || stats.top_outstanding.length === 0) && (
            <div className="pl-empty">Nothing outstanding — great work.</div>
          )}
          {stats.top_outstanding?.map((row, i) => (
            <div
              key={i}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: i < stats.top_outstanding.length - 1 ? "1px solid var(--line)" : "none" }}
            >
              <div>
                <Link to={`/customers/${row.customer_id}`} style={{ fontWeight: 700, fontSize: 13.5, color: "inherit", textDecoration: "none" }}>
                  {row.customer_name}
                </Link>
                <div className="pl-meta">{row.equipment_name}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>{naira(row.balance)}</div>
                {row.is_overdue && <span className="pl-chip pl-chip-red">Overdue</span>}
                {!row.is_overdue && row.is_due_soon && <span className="pl-chip pl-chip-amber">Due soon</span>}
              </div>
            </div>
          ))}
        </div>

        <div className="pl-panel" style={{ padding: 20 }}>
          <div className="pl-section-title">Collections — last 14 days</div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={series} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="collectionsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tickFormatter={(d) => formatDate(d).replace(/,.*/, "")} tick={{ fontSize: 10.5, fill: "#8892a6" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => naira(v)} labelFormatter={(d) => formatDate(d)} />
              <Area type="monotone" dataKey="amount" stroke="#2563eb" strokeWidth={2} fill="url(#collectionsFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="pl-panel" style={{ padding: 20 }}>
        <div className="pl-section-title">Recent Activity</div>
        {(!stats.recent_activity || stats.recent_activity.length === 0) && <div className="pl-empty">Nothing yet.</div>}
        {stats.recent_activity?.map((a, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: i < stats.recent_activity.length - 1 ? "1px solid var(--line)" : "none" }}>
            {a.type === "payment" ? <CheckCircle2 size={16} color="var(--green-600)" /> : <PlusCircle size={16} color="var(--blue-600)" />}
            <div style={{ fontSize: 13, flex: 1 }}>{a.text}</div>
            <div className="pl-meta" style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Clock size={11} /> {formatDate(a.timestamp)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}